import http from "@/shared/services/api-client";
import {
  BadAuthCredentialsError,
  JsonParsingError,
  UnexpectedError,
} from "@/shared/utils/custom-errors";

import {
  adminV1OrganizationsExercisesCreate,
  adminV1OrganizationsExercisesDestroy,
  adminV1OrganizationsExercisesList,
  adminV1OrganizationsExercisesPartialUpdate,
  adminV1OrganizationsExercisesRetrieve,
} from "@/api/admin-exercises/admin-exercises";
import {
  adminV1OrganizationsWorkoutsPartialUpdate,
  adminV1OrganizationsWorkoutsDestroy,
  adminV1OrganizationsWorkoutsCreate,
  adminV1OrganizationsWorkoutsList,
  adminV1OrganizationsWorkoutsRetrieve,
  adminV1OrganizationsWorkoutsCopyWorkoutCreate,
} from "@/api/admin-workouts/admin-workouts";
import {
  adminV1OrganizationsCalendarsCreate,
  adminV1OrganizationsCalendarsDestroy,
  adminV1OrganizationsCalendarsList,
  adminV1OrganizationsCalendarsPartialUpdate,
} from "@/api/admin-calendars/admin-calendars";
import {
  adminV1OrganizationsBlocksExercisesCreate,
  adminV1OrganizationsBlocksExercisesPartialUpdate,
  adminV1OrganizationsBlocksExercisesDestroy,
} from "@/api/admin-block-exercises/admin-block-exercises";
import type {
  AdminBlockExerciseCreate,
  AdminV1OrganizationsWorkoutsListParams,
  AdminV1OrganizationsWorkoutsTypesListParams,
  AdminCalendar,
  AdminWorkout,
  AdminWorkoutsExercise,
  AdminBlock,
  CopyWorkoutResponse,
  PaginatedAdminCalendarList,
  AdminSet,
  AdminBlockExercise,
  SwaggerAdminWorkoutList,
  PaginatedAdminWorkoutTypeList,
  AdminV1OrganizationsExercisesListParams,
  AdminV1OrganizationsCalendarsListParams,
  PatchedAdminWorkoutsExercise,
  AdminBlockCreate,
  PatchedAdminCalendar,
  AdminV1OrganizationsSelectedWorkoutTypesListParams,
  PaginatedSelectedWorkoutTypeList,
  SelectedWorkoutType,
} from "@/api/model";
import { adminV1OrganizationsWorkoutsTypesList } from "@/api/admin-workout-types/admin-workout-types";
import {
  adminV1OrganizationsBlocksCreate,
  adminV1OrganizationsBlocksPartialUpdate,
  adminV1OrganizationsBlocksDestroy,
} from "@/api/admin-blocks/admin-blocks";
import {
  adminV1OrganizationsSelectedWorkoutTypesCreate,
  adminV1OrganizationsSelectedWorkoutTypesList,
} from "@/api/admin-workout-type-selected/admin-workout-type-selected";
import { EVENT_NAMES, addMixpanelEvent } from "../../shared/utils/analytics";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";

function getDefaultOrganizationId(): number {
  const { currentOrganizationId } = useCurrentOrganization();
  const orgId = currentOrganizationId.value;
  if (!orgId) {
    throw new Error("No default organization found");
  }
  return orgId;
}

interface errorMessage {
  error: string;
}

export const getWorkouts = async (
  filter: AdminV1OrganizationsWorkoutsListParams
): Promise<SwaggerAdminWorkoutList | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const results = await adminV1OrganizationsWorkoutsList(
      organizationId,
      filter
    );
    return results;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const cloneWorkout = async (
  workoutId: number,
  dayToPaste: number,
  calendarId?: number
): Promise<CopyWorkoutResponse | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const results = await adminV1OrganizationsWorkoutsCopyWorkoutCreate(
      organizationId,
      {
        workout_id: workoutId,
        day: dayToPaste,
        calendar_id: calendarId,
      }
    );
    return results;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getWorkout = async (
  workoutId: number
): Promise<AdminWorkout | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const results = await adminV1OrganizationsWorkoutsRetrieve(
      organizationId,
      workoutId
    );
    return results;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const createWorkout = async (
  data: Pick<
    AdminWorkout,
    "calendar_id" | "status" | "instructions" | "day" | "organization_id"
  >
): Promise<AdminWorkout | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const results = await adminV1OrganizationsWorkoutsCreate(
      organizationId,
      data
    );
    return results;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateWorkout = async (
  workoutId: number,
  data: Record<string, unknown>
): Promise<AdminWorkout | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const results = await adminV1OrganizationsWorkoutsPartialUpdate(
      organizationId,
      workoutId,
      data
    );
    return results;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const deleteWorkout = async (workoutId: number) => {
  try {
    addMixpanelEvent(EVENT_NAMES.Workouts.WorkoutDeleted);
    const organizationId = getDefaultOrganizationId();
    const results: unknown = await adminV1OrganizationsWorkoutsDestroy(
      organizationId,
      workoutId
    );
    return results as boolean;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getExercises = async (
  args: AdminV1OrganizationsExercisesListParams
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const exercises = await adminV1OrganizationsExercisesList(
      organizationId,
      args
    );
    return exercises;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getExercise = async (exerciseId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const exercises = await adminV1OrganizationsExercisesRetrieve(
      organizationId,
      exerciseId
    );
    return exercises;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const createExercise = async (
  form: Omit<
    AdminWorkoutsExercise,
    "video" | "updated_at" | "created_at" | "id"
  >
) => {
  addMixpanelEvent(EVENT_NAMES.Exercises.ExerciseCreated);
  try {
    const organizationId = getDefaultOrganizationId();
    // Remove organization field from form since it's specified in the URL path
    const { organization, ...exerciseData } = form;
    const exercises = await adminV1OrganizationsExercisesCreate(
      organizationId,
      exerciseData
    );
    return exercises;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateExercise = async (
  exerciseId: number,
  form: PatchedAdminWorkoutsExercise
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const exercises = await adminV1OrganizationsExercisesPartialUpdate(
      organizationId,
      exerciseId,
      form
    );
    return exercises;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const deleteExercise = async (exerciseId: number) => {
  addMixpanelEvent(EVENT_NAMES.Exercises.ExerciseDeleted);
  try {
    const organizationId = getDefaultOrganizationId();
    const exercises = await adminV1OrganizationsExercisesDestroy(
      organizationId,
      exerciseId
    );
    return exercises;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getCalendars = async (
  filter: AdminV1OrganizationsCalendarsListParams
): Promise<PaginatedAdminCalendarList | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsCalendarsList(
      organizationId,
      filter
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const createCalendar = async (
  data: Pick<AdminCalendar, "type_name" | "with_weights" | "is_private">
): Promise<AdminCalendar | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsCalendarsCreate(
      organizationId,
      data
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const editCalendar = async (
  id: number,
  data: PatchedAdminCalendar
): Promise<AdminCalendar | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsCalendarsPartialUpdate(
      organizationId,
      id,
      data
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const deleteCalendar = async (
  id: number
): Promise<void | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsCalendarsDestroy(
      organizationId,
      id
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getWorkoutTypes = async (
  filter: AdminV1OrganizationsWorkoutsTypesListParams
): Promise<PaginatedAdminWorkoutTypeList | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsWorkoutsTypesList(organizationId, {
      limit: filter?.limit,
      offset: filter?.offset,
      search: filter.search ?? "",
    });
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const assignWorkoutCalendar = async (
  data: Pick<
    SelectedWorkoutType,
    "profile_id" | "workout_type_id" | "seed_date"
  >
): Promise<SelectedWorkoutType | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsSelectedWorkoutTypesCreate(
      organizationId,
      data
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getSelectedWorkoutTypes = async (
  data: AdminV1OrganizationsSelectedWorkoutTypesListParams
): Promise<PaginatedSelectedWorkoutTypeList | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsSelectedWorkoutTypesList(
      organizationId,
      data
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getAssignedWorkoutCalendar = async (
  data: AdminV1OrganizationsSelectedWorkoutTypesListParams
): Promise<PaginatedSelectedWorkoutTypeList | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsSelectedWorkoutTypesList(
      organizationId,
      data
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const postBlock = async (
  data: Pick<AdminBlockCreate, "order" | "workout_id">
): Promise<AdminBlockCreate | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsBlocksCreate(organizationId, data);
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateBlock = async (
  id: number,
  data: Record<string, unknown>
): Promise<AdminBlock | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsBlocksPartialUpdate(
      organizationId,
      id,
      data
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const postBlockExercise = async (
  data: Pick<
    AdminBlockExerciseCreate,
    "exercise_id" | "order" | "with_weights" | "block_id"
  >
): Promise<AdminBlockExerciseCreate | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const exercises = await adminV1OrganizationsBlocksExercisesCreate(
      organizationId,
      data
    );
    return exercises;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateBlockExercise = async (
  id: number,
  data: Record<string, unknown>
): Promise<AdminBlockExercise | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsBlocksExercisesPartialUpdate(
      organizationId,
      id,
      data
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const deleteBlockExercise = async (
  id: number | string
): Promise<boolean | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsBlocksExercisesDestroy(
      organizationId,
      Number(id)
    );
    return true;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const deleteBlock = async (
  id: number | string
): Promise<boolean | errorMessage> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsBlocksDestroy(organizationId, Number(id));
    return true;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const postSet = async (
  data: Record<string, unknown>
): Promise<AdminSet> => {
  const organizationId = getDefaultOrganizationId();
  const res = await http.post(
    `/api/admin/v1/organizations/${organizationId}/sets/`,
    data
  );
  if (res?.status == 401) throw new BadAuthCredentialsError();
  if (res?.status != 201) throw new UnexpectedError();
  try {
    return res?.data as AdminSet;
  } catch (e) {
    throw new JsonParsingError();
  }
};

export const updateSet = async (
  id: number,
  data: Record<string, unknown>
): Promise<AdminSet> => {
  const organizationId = getDefaultOrganizationId();
  const res = await http.put(
    `/api/admin/v1/organizations/${organizationId}/sets/${id}/`,
    data
  );
  if (res?.status == 401) throw new BadAuthCredentialsError();
  if (res?.status != 200) throw new UnexpectedError();
  try {
    return res?.data as AdminSet;
  } catch (e) {
    throw new JsonParsingError();
  }
};

export const deleteSet = async (id: number | string): Promise<boolean> => {
  const organizationId = getDefaultOrganizationId();
  const res = await http.delete(
    `/api/admin/v1/organizations/${organizationId}/sets/${id}/`
  );
  if (res?.status == 401) throw new BadAuthCredentialsError();
  if (res?.status != 204) throw new UnexpectedError();
  try {
    return true;
  } catch (e) {
    throw new JsonParsingError();
  }
};
